import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  ArrowDownward,
  ArrowUpward,
  DeleteOutline,
  EditOutlined,
  PeopleOutline,
  Search,
} from "@mui/icons-material";
import MDButton from "shared/components/MDButton";
import DataTable from "shared/components/Tables/DataTable";
import {
  getUsersWithRolesService,
  putUserService,
  updateUserService,
} from "modules/usuario/services/userService";
import UserDialog from "../Form/UserDialog";

const filterInputSx = {
  "& .MuiInputBase-root": { minHeight: 54, height: "auto", fontSize: 15, borderRadius: 2 },
  "& .MuiInputBase-input": { fontSize: "15px !important" },
  "& .MuiOutlinedInput-input": { py: "16px !important" },
  "& .MuiSelect-select": { py: "16px !important", lineHeight: 1.5 },
  "& .MuiInputLabel-root": { fontSize: 14 },
};

export default function TablaUser({ refreshFlag = false, onCreate }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [descending, setDescending] = useState(false);
  const [notification, setNotification] = useState(null);

  const loadUsers = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getUsersWithRolesService();
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || "Error al cargar usuarios");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    loadUsers();
  }, [refreshFlag]);

  const roles = useMemo(() => {
    const unique = new Map();
    users.forEach((user) => {
      if (user.roleId != null)
        unique.set(String(user.roleId), user.role?.name || `Rol ${user.roleId}`);
    });
    return Array.from(unique, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [users]);

  const filtered = useMemo(() => {
    const term = search.trim().toLocaleLowerCase();
    return users
      .filter(
        (user) =>
          (!roleFilter || String(user.roleId) === roleFilter) &&
          (statusFilter === "" || Number(user.status) === Number(statusFilter)) &&
          (!term ||
            [user.id, user.username, user.email, user.role?.name, user.roleId].some((value) =>
              String(value ?? "")
                .toLocaleLowerCase()
                .includes(term)
            ))
      )
      .sort(
        (a, b) =>
          (descending ? -1 : 1) *
          String(a.id).localeCompare(String(b.id), undefined, { numeric: true })
      );
  }, [users, search, roleFilter, statusFilter, descending]);

  const update = async (values) => {
    await updateUserService(selectedUser.id, {
      username: values.username,
      email: values.email,
      roleId: values.roleId,
      status: values.status,
      ...(values.more !== undefined ? { more: values.more } : {}),
    });
    await loadUsers();
    setSelectedUser(null);
    setNotification({ severity: "success", message: "Usuario actualizado correctamente" });
  };

  const remove = async () => {
    if (!deleteUser || deleting) return;
    setDeleting(true);
    try {
      await putUserService(deleteUser.id);
      setDeleteUser(null);
      await loadUsers();
      setNotification({ severity: "success", message: "Solicitud de eliminación completada" });
    } catch (err) {
      setNotification({ severity: "error", message: err.message || "Error al eliminar usuario" });
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    { Header: "ID", accessor: "id", width: "7%", align: "center" },
    { Header: "Usuario", accessor: "user", width: "23%", align: "left" },
    { Header: "Correo", accessor: "email", width: "26%", align: "left" },
    { Header: "Rol", accessor: "role", width: "22%", align: "left" },
    { Header: "Estado", accessor: "status", width: "10%", align: "center" },
    { Header: "Acciones", accessor: "actions", width: "12%", align: "center" },
  ];
  const rows = filtered.map((user) => ({
    id: (
      <Chip
        label={`#${user.id}`}
        size="small"
        sx={{ bgcolor: "action.hover", fontSize: ".7rem", height: 22 }}
      />
    ),
    user: (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
        <Avatar
          src={user.avatar}
          sx={{ width: 32, height: 32, bgcolor: "info.main", fontSize: ".8rem" }}
        >
          {user.username?.charAt(0).toUpperCase()}
        </Avatar>
        <Typography variant="button" fontWeight="medium" sx={{ textTransform: "none" }}>
          {user.username || "Sin nombre"}
        </Typography>
      </Box>
    ),
    email: (
      <Typography variant="caption" sx={{ overflowWrap: "anywhere" }}>
        {user.email || "Sin correo"}
      </Typography>
    ),
    role: (
      <Chip
        label={user.role?.name || "Sin rol"}
        variant="outlined"
        size="small"
        sx={{
          height: "auto",
          minHeight: 25,
          maxWidth: 240,
          "& .MuiChip-label": { whiteSpace: "normal", py: 0.5 },
        }}
      />
    ),
    status: (
      <Chip
        label={Number(user.status) === 1 ? "Activo" : "Inactivo"}
        size="small"
        variant="outlined"
        color={Number(user.status) === 1 ? "success" : "default"}
      />
    ),
    actions: (
      <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
        <Tooltip title="Editar usuario">
          <IconButton
            aria-label={`Editar ${user.username}`}
            onClick={() => setSelectedUser(user)}
            size="small"
            sx={{ color: "info.main", bgcolor: "rgba(26,115,232,.08)" }}
          >
            <EditOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Eliminar usuario">
          <IconButton
            aria-label={`Eliminar ${user.username}`}
            onClick={() => setDeleteUser(user)}
            size="small"
            sx={{ color: "error.main", bgcolor: "rgba(244,67,54,.06)" }}
          >
            <DeleteOutline fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    ),
  }));

  return (
    <>
      <Paper elevation={1} sx={{ overflow: "hidden", borderRadius: 2 }}>
        <Box
          sx={{
            px: 3,
            py: 2.5,
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            color: "#fff",
            background: "linear-gradient(120deg, #123149, #155e66)",
          }}
        >
          <PeopleOutline sx={{ color: "#fff !important" }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" color="inherit">
              Gestión de Usuarios
            </Typography>
            <Typography variant="caption" color="inherit">
              {users.length} usuarios · {users.filter((user) => Number(user.status) === 1).length}{" "}
              activos
            </Typography>
          </Box>
          <MDButton variant="contained" color="white" startIcon={<Add />} onClick={onCreate}>
            Crear usuario
          </MDButton>
        </Box>
        <Box
          sx={{
            p: 2,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1.5,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <TextField
            label="Buscar usuario"
            placeholder="Nombre, correo, rol o ID"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{ ...filterInputSx, flex: "1 1 320px" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            label="Rol"
            InputLabelProps={{ shrink: true }}
            SelectProps={{
              displayEmpty: true,
              renderValue: (value) =>
                roles.find((role) => role.id === value)?.name || "Todos los roles",
              SelectDisplayProps: { "aria-label": "Filtrar por rol" },
            }}
            value={roleFilter}
            onChange={(event) => setRoleFilter(event.target.value)}
            sx={{
              ...filterInputSx,
              flex: "1 1 200px",
              maxWidth: { sm: 280 },
            }}
          >
            <MenuItem value="">Todos los roles</MenuItem>
            {roles.map((role) => (
              <MenuItem key={role.id} value={role.id}>
                {role.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Estado"
            InputLabelProps={{ shrink: true }}
            SelectProps={{
              displayEmpty: true,
              renderValue: (value) =>
                value === "1" ? "Activos" : value === "0" ? "Inactivos" : "Todos",
              SelectDisplayProps: { "aria-label": "Filtrar por estado" },
            }}
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            sx={{
              ...filterInputSx,
              minWidth: 165,
              flex: { xs: "1 1 165px", sm: "0 1 180px" },
            }}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="1">Activos</MenuItem>
            <MenuItem value="0">Inactivos</MenuItem>
          </TextField>
          <Tooltip title={descending ? "Ordenar por ID ascendente" : "Ordenar por ID descendente"}>
            <IconButton
              aria-label="Cambiar orden por ID"
              onClick={() => setDescending((value) => !value)}
              color="info"
            >
              {descending ? <ArrowDownward /> : <ArrowUpward />}
            </IconButton>
          </Tooltip>
          {(search || roleFilter || statusFilter) && (
            <MDButton
              variant="text"
              color="secondary"
              onClick={() => {
                setSearch("");
                setRoleFilter("");
                setStatusFilter("");
              }}
            >
              Limpiar
            </MDButton>
          )}
        </Box>
        {loading && <LinearProgress aria-label="Cargando usuarios" />}
        {error && (
          <Alert
            severity="error"
            sx={{ m: 2 }}
            action={
              <MDButton color="error" onClick={loadUsers}>
                Reintentar
              </MDButton>
            }
          >
            {error}
          </Alert>
        )}
        <Box
          sx={{
            overflowX: "auto",
            "& td": { py: 1.25 },
            "& thead th > div": { color: "text.primary", opacity: 1, fontSize: "12px" },
            "& tbody td .MuiTypography-root": {
              fontSize: "15px !important",
              color: (theme) =>
                theme.palette.mode === "dark" ? "#f1f5f9 !important" : "#111827 !important",
              fontWeight: "600 !important",
              opacity: 1,
            },
            "& tbody .MuiChip-label": { fontSize: "13px" },
            "& tbody td:nth-of-type(1) .MuiChip-label, & tbody td:nth-of-type(4) .MuiChip-label": {
              color: (theme) =>
                theme.palette.mode === "dark" ? "#f1f5f9 !important" : "#111827 !important",
              fontWeight: 600,
            },
            "& tbody tr:hover": { bgcolor: "action.hover" },
          }}
        >
          <DataTable
            table={{ columns, rows }}
            isSorted={false}
            entriesPerPage={{ defaultValue: 10, entries: [5, 10, 25, 50, 100] }}
            showTotalEntries
            noEndBorder
            canSearch={false}
            autoResetPage={false}
            paginationResetKey={JSON.stringify([search, roleFilter, statusFilter, descending])}
          />
        </Box>
        {!loading && !error && !filtered.length && (
          <Typography variant="body2" sx={{ px: 3, pb: 3 }}>
            No hay usuarios que coincidan con los filtros.
          </Typography>
        )}
      </Paper>
      <UserDialog
        open={Boolean(selectedUser)}
        user={selectedUser}
        onClose={() => setSelectedUser(null)}
        onSubmit={update}
      />
      <Dialog
        open={Boolean(deleteUser)}
        onClose={() => !deleting && setDeleteUser(null)}
        fullWidth
        maxWidth="xs"
        aria-labelledby="delete-user-title"
      >
        <DialogTitle id="delete-user-title">Eliminar usuario</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            ¿Deseas eliminar al usuario <strong>{deleteUser?.username}</strong> (ID {deleteUser?.id}
            )?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <MDButton disabled={deleting} onClick={() => setDeleteUser(null)} color="secondary">
            Cancelar
          </MDButton>
          <MDButton disabled={deleting} onClick={remove} color="error" variant="contained">
            {deleting ? <CircularProgress size={20} color="inherit" /> : "Eliminar"}
          </MDButton>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(notification)}
        autoHideDuration={5000}
        onClose={() => setNotification(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={notification?.severity || "success"} onClose={() => setNotification(null)}>
          {notification?.message}
        </Alert>
      </Snackbar>
    </>
  );
}
TablaUser.propTypes = { refreshFlag: PropTypes.bool, onCreate: PropTypes.func };
